import React from 'react';
import WizardCard from './WizardCard';
import WizardBreadcrumb from './WizardBreadcrumb';
import WizardNavigation from './WizardNavigation';
import OptionGrid from './OptionGrid';

import {
  WizardSelection,
  WizardStepType,
  WizardStepData,
  STEP_ORDER,
} from '@/components/wizard/types';

interface WizardStepProps {
  currentStepData: WizardStepData;
  selection: WizardSelection;
  owner?: {
    id: number;
    title: string;
    display_id?: string;
  };
  hiddenSteps?: WizardStepType[];

  // Actions
  onSelect: (option: string) => void;
  onBack?: () => void;
  onNext?: () => void;
  onReset?: () => void;
}

const WizardStep: React.FC<WizardStepProps> = ({
  currentStepData,
  selection,
  owner,
  hiddenSteps = [],

  // Actions
  onSelect,
  onBack,
  onNext,
  onReset,
}) => {
  const header = (
    <>
      {owner && (
        <div className="mb-4 flex flex-col">
          <span className="text-lg font-semibold text-gray-900">
            {owner.title}
          </span>
          {owner.display_id && (
            <span className="text-gray-500">
              Parent Submission ID: {owner.display_id}
            </span>
          )}
        </div>
      )}
      <WizardBreadcrumb selection={selection} hiddenSteps={hiddenSteps} />
    </>
  );

  const handleBack = () => {
    if (STEP_ORDER.indexOf(currentStepData.currentStep) > 0) {
      onBack?.();
    }
  };

  const handleNext = () => {
    if (
      STEP_ORDER.indexOf(currentStepData.currentStep) <
      STEP_ORDER.length - 1
    ) {
      onNext?.();
    }
  };

  const isFirstStep = STEP_ORDER.indexOf(currentStepData.currentStep) === 0;

  // Single-select steps auto-advance on click, so they need no Continue button. A
  // multi-select step (OV-1180) does — the user keeps ticking boxes until they say so.
  const footer = (
    <WizardNavigation
      onBack={!isFirstStep ? handleBack : undefined}
      onSubmit={currentStepData.allowMultiple ? handleNext : undefined}
      onReset={onReset}
      disabled={currentStepData.selected.length === 0}
    />
  );

  // OV-1180: Translation and Appeal are reviewed on their own, so the grid greys out
  // whatever cannot be combined with what is already ticked. Say why rather than
  // leaving the user to work out what changed — and note the two cases need opposite
  // explanations: either the greyed options are themselves exclusive, or they are
  // ordinary options ruled out because an exclusive one is already selected.
  const showsExclusivityHint =
    currentStepData.allowMultiple &&
    (currentStepData.disabledOptions?.length ?? 0) > 0;
  const exclusivityHint = currentStepData.disabledByExclusiveSelection
    ? 'Your selection must be submitted on its own, so the other options are unavailable.'
    : 'Some options are unavailable because they must be submitted on their own.';

  return (
    <WizardCard header={header} footer={footer}>
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-6">
          {currentStepData.title}
        </h2>
        {currentStepData.allowMultiple && (
          <p className="text-sm text-gray-600 -mt-4">
            Select all that apply. The questions for each will be combined into
            one submission.
          </p>
        )}
        {showsExclusivityHint && (
          <p className="text-sm text-gray-500 mt-2">{exclusivityHint}</p>
        )}
      </div>

      <OptionGrid
        options={currentStepData.options ?? []}
        selectedOptions={currentStepData.selected}
        allowMultiple={currentStepData.allowMultiple}
        disabledOptions={currentStepData.disabledOptions}
        onSelect={onSelect}
      />
    </WizardCard>
  );
};

export default WizardStep;
